import { acceptableExtensions } from "@teamprevent/common/acceptableExtensions";
import { FileUpload } from "@teamprevent/common/fileUploads";
import { TaskModel } from "@teamprevent/common/task/taskActions/models";

import * as moment from "moment";
import * as React from "react";
import { connect } from "react-redux";

import { Button, Icon, Item, Label, List, ListItem, Modal } from "semantic-ui-react";

import { ApplicationState } from "../../../../applicationState";
import { cancelTask, createOhCaseDocumentSuccess } from "../../actions";
import { PartialOhCaseDetailModel, OhCaseDocument, sortTasks } from "../../model";
import { routeIdSelector as ohCaseIdSelector } from "../../selectors";

import { CloseCaseModalItem } from "./CloseCaseModalItem";

import { push } from "redux-little-router";

interface OwnState {
    documentNames: string[];
    tasks: TaskModel[];
    open: boolean;
}

interface OwnProps {
    ohCase: PartialOhCaseDetailModel;
    customerId: string;
}

interface StateProps {
    ohCaseId: string;
}

interface DispatchProps {
    dispatchPush: (path: string) => void;
    createDocumentSuccess: (ohCaseId: string, ohCaseDocument: OhCaseDocument) => Promise<void>;
    onCancelTask: (ohCaseId: string, taskId: string, closingNote: string) => Promise<TaskModel[]>;
}

interface MergedProps {
    ohCaseId: string;
    createDocumentSuccess: (ohCaseDocument: OhCaseDocument) => Promise<void>;
    onCancelTask: (taskId: string, closingNote: string) => Promise<TaskModel[]>;
}

type Props = OwnProps & MergedProps;

class CloseCaseModalUnconnected extends React.Component<Props, OwnState> {

    constructor(props: Props) {
        super(props);
        this.state = {
            open: false,
            documentNames: [],
            tasks: (this.props.ohCase && this.props.ohCase.tasks) ? this.props.ohCase.tasks
                .filter(task => task.cancelled === false && task.completed === false) : []
        };
    }

    public componentDidUpdate(prevProps: Props) {
        if (this.state.tasks.length === 0 && this.props.ohCase !== prevProps.ohCase) {
            this.initaliseTaskState();
        }
    }

    public render() {
        return (
            <>
                <Modal size="small" open={this.state.open} closeOnDimmerClick={false}>
                    <Modal.Header>Open Tasks</Modal.Header>
                    <Modal.Content>
                        <Item.Group divided>
                            {sortTasks(this.state.tasks)
                                .map(task => (<CloseCaseModalItem
                                    task={task}
                                    key={task.id}
                                    customerId={this.props.customerId}
                                    onCancelTask={this.onCancelTask}
                                />))}
                        </Item.Group>
                        <Item.Group>
                            {this.state.documentNames && this.state.documentNames.map((name, i) =>
                                (<Label as="a" key={i}>
                                    <Icon name="file" />{name}
                                </Label>)
                            )}
                        </Item.Group>
                    </Modal.Content>
                    <Modal.Actions>
                        <List horizontal>
                            <ListItem>
                                <Button onClick={this.onCloseModal} content="Go Back" />
                            </ListItem>
                            <ListItem>
                                <FileUpload
                                    showFileName={false}
                                    // eslint-disable-next-line max-len
                                    apiUrl={`/api/customer/${this.props.customerId}/case/${this.props.ohCaseId}/document?documentType=${encodeURIComponent("General")}`}
                                    onFileChange={this.onDocumentUploaded}
                                    label={"Add Document"}
                                    popupText={""}
                                    acceptExtensions={acceptableExtensions}
                                />
                            </ListItem>
                        </List>

                    </Modal.Actions>
                </Modal>
                <Button className="warning" content="Close Tasks" onClick={this.onOpenModal} />
            </>
        );
    }

    private initaliseTaskState() {
        this.setState({
            tasks: this.props.ohCase.tasks
                .filter(task => task.cancelled === false && task.completed === false)
        });
    }

    private onOpenModal = () => {
        this.initaliseTaskState();
        this.setState({ open: true, documentNames: [] });
    };

    private onCloseModal = () => this.setState({ open: false });

    private onCancelTask = async (taskId: string, closingNote: string) => {
        const updateTasks = await this.props.onCancelTask(taskId, closingNote);
        this.setState(prevState => ({ tasks: [...prevState.tasks.filter(t => t.id !== taskId), updateTasks.find(t => t.id === taskId)] }));
    };

    private onDocumentUploaded = (ohCaseDocumentId: string, fileName: string) => {
        this.props.createDocumentSuccess({ id: ohCaseDocumentId, name: fileName, documentType: "General", dateCreated: moment.utc() });
        this.setState(prevState => ({
            documentNames: [...prevState.documentNames, fileName]
        }));
    };
}

const mapStateToProps = (state: ApplicationState): StateProps => ({
    ohCaseId: ohCaseIdSelector(state)
});

const mapDispatchToProps = (dispatch: any): DispatchProps => ({
    dispatchPush: (path: string) => dispatch(push(path)),
    createDocumentSuccess: (ohCaseId: string, ohCaseDocument: OhCaseDocument) =>
        dispatch(createOhCaseDocumentSuccess({ ohCaseId, document: ohCaseDocument })),
    onCancelTask: (ohCaseId: string, taskId: string, closingNote: string) =>
        dispatch(cancelTask(ohCaseId, taskId, closingNote))
});

const mergeProps = (stateProps: StateProps, dispatchProps: DispatchProps, ownProps: OwnProps): MergedProps => {
    const { ohCaseId, ...propsFromState } = stateProps;
    return {
        ohCaseId,
        ...ownProps,
        ...propsFromState,
        createDocumentSuccess: (ohCaseDocument: OhCaseDocument) => dispatchProps.createDocumentSuccess(ohCaseId, ohCaseDocument),
        onCancelTask: (taskId: string, closingNote: string) => dispatchProps.onCancelTask(ohCaseId, taskId, closingNote)
    };
};

export const CloseCaseModal = connect(mapStateToProps, mapDispatchToProps, mergeProps)(CloseCaseModalUnconnected);
